import React, { useState } from 'react';
import { AlertTriangleIcon, FileTextIcon, Loader2Icon, LockIcon, PhoneIcon, CreditCardIcon, LandmarkIcon } from 'lucide-react';
import { STEP_ESTIMATE, useFunnel } from '../../contexts/FunnelContext';
import { phones, siteConfig, isLive } from '../../data/siteConfig';
import type { StringKey } from '../../data/i18n';
import type { StepErrors } from '../../utils/validation';
import { stepErrors } from '../../utils/validation';
import { usd } from '../../utils/format';
import { fireAddToCart } from '../../utils/metaPixel';
import { ChoiceCard } from './ChoiceCard';
import { TextField } from './TextField';
import { HelpAnswers } from './HelpAnswers';
interface DocRow {
  key: StringKey;
  who: string;
  when: StringKey;
}
export function StepReserve() {
  const {
    t,
    lang,
    data,
    update,
    quote,
    estimateAccepted,
    goTo,
    submit
  } = useFunnel();
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errors, setErrors] = useState<StepErrors>({});
  const checkoutReady = isLive() && siteConfig.depositCheckoutEnabled;
  if (!estimateAccepted) {
    return <div className="rounded-2xl border-2 border-magenta/40 bg-white p-6" data-id="element-867" data-mp="1gsaocvkirjpa">
        <AlertTriangleIcon className="h-8 w-8 text-magenta" aria-hidden="true" data-id="element-868" data-mp="1bvu081dbl5c7" />
        <h1 className="mt-3 font-display text-2xl font-black text-ink" data-id="element-869" data-mp="16w7m7ppo3l2c">{t('staleTitle')}</h1>
        <p className="mt-2 text-[15px] text-ink-600" data-id="element-870" data-mp="24j7o79z1iqk6">{t('staleBody')}</p>
        <button type="button" onClick={() => goTo(STEP_ESTIMATE)} className="mt-5 h-12 rounded-xl bg-ink px-5 font-semibold text-white hover:bg-ink-700" data-id="element-871" data-mp="24h3nf0wfu7o4">
          {t('staleBtn')}
        </button>
      </div>;
  }
  const docs: DocRow[] = [{
    key: 'docBooking',
    who: t('whoYou'),
    when: 'whenNow'
  }, ...(data.paymentMethod === 'card' ? [data.thirdPartyPayer ? {
    key: 'docThird' as StringKey,
    who: `${t('whoPayer')}${data.payerName ? ` — ${data.payerName}` : ''}`,
    when: 'whenNow' as StringKey
  } : {
    key: 'docDeposit' as StringKey,
    who: t('whoCardholder'),
    when: 'whenNow' as StringKey
  }] : []), {
    key: 'docFull',
    who: data.thirdPartyPayer ? t('whoPayer') : t('whoCardholder'),
    when: 'whenLater'
  }, {
    key: 'docBol',
    who: t('whoYou'),
    when: 'whenLater'
  }];
  const goShopify = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const url = siteConfig.shopifyCheckoutUrl;
    if (!url) return;
    e.preventDefault();
    fireAddToCart(quote.deposit, `atc_${quote.hash}_${Date.now()}`);
    // Short delay so the pixel request leaves before the page navigates away.
    window.setTimeout(() => window.location.assign(url), 300);
  };
  const send = async () => {
    const e = stepErrors(8, data);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSending(true);
    setFailed(false);
    try {
      await submit();
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };
  return <div data-id="element-872" data-mp="3f215kw8emmv6">
      <div className="mb-7" data-id="element-873" data-mp="3f2swvuuyxyfn">
        <h1 className="font-display text-3xl font-black leading-tight text-magenta sm:text-4xl" data-id="element-874" data-mp="3f55iqj3uksxz">{t('reserveTitle')}</h1>
        <p className="mt-2 text-base text-ink-600" data-id="element-875" data-mp="2rggzhh774xky">{t('reserveIntro')}</p>
        <p className="mt-2 font-mono text-xs text-ink-500" data-id="element-876" data-mp="2ria995d3vxx6">
          {t('versionLabel')}: {quote.configVersion} · #{quote.hash}
        </p>
      </div>

      <section className="grid gap-6 md:grid-cols-2" data-id="element-877" data-mp="30fkmuxdebz59">
        <fieldset data-id="element-878" data-mp="3fdwegplwwhpk">
          <legend className="mb-2 font-display text-lg font-extrabold text-ink" data-id="element-879" data-mp="3fdcuz4gm5l9f">{t('payerTitle')}</legend>
          <div role="radiogroup" aria-label={t('payerTitle')} className="grid gap-2" data-id="element-880" data-mp="3afl6ylj3ajai">
            <ChoiceCard size="sm" selected={!data.thirdPartyPayer} onSelect={() => update({
            thirdPartyPayer: false
          })} title={t('payerMe')} data-id="element-881" data-mp="3o6py0z8e6398" />
            <ChoiceCard size="sm" selected={data.thirdPartyPayer} onSelect={() => update({
            thirdPartyPayer: true
          })} title={t('payerOther')} data-id="element-882" data-mp="3k54onnv2g73s" />
          </div>
          {data.thirdPartyPayer && <div className="mt-4 space-y-3" data-id="element-883" data-mp="3t6ug2toy761k">
              <TextField label={t('payerName')} value={data.payerName} onChange={v => update({
            payerName: v
          })} data-id="element-884" data-mp="h1zylv6sod1z" />
              <TextField label={t('payerEmail')} type="email" value={data.payerEmail} onChange={v => update({
            payerEmail: v
          })} data-id="element-885" data-mp="l3387zy91cur" />
              <TextField label={t('payerPhone')} type="tel" value={data.payerPhone} onChange={v => update({
            payerPhone: v
          })} data-id="element-886" data-mp="pvmqha7vsw5w" />
              {errors.payer && <p className="text-sm font-medium text-magenta-dark" data-id="element-887" data-mp="3u8jjbjxtflc5">{t(errors.payer)}</p>}
            </div>}
        </fieldset>
        <fieldset data-id="element-888" data-mp="25scgpbtt1k2">
          <legend className="mb-2 font-display text-lg font-extrabold text-ink" data-id="element-889" data-mp="2593vz82fpip">{t('methodTitle')}</legend>
          <div role="radiogroup" aria-label={t('methodTitle')} className="grid gap-2" data-id="element-890" data-mp="72iq7605rl2b">
            <ChoiceCard size="sm" selected={data.paymentMethod === 'card'} onSelect={() => update({
            paymentMethod: 'card'
          })} title={t('payCard')} icon={<CreditCardIcon className="h-5 w-5" data-id="element-892" data-mp="1ni3jw4dhupmd" />} data-id="element-891" data-mp="c4hvilfgks1w" />
            <ChoiceCard size="sm" selected={data.paymentMethod === 'zelle'} onSelect={() => update({
            paymentMethod: 'zelle'
          })} title={t('payZelle')} icon={<LandmarkIcon className="h-5 w-5" data-id="element-894" data-mp="3311g7c84b8lh" />} data-id="element-893" data-mp="1ngsdrxwkt5s3" />
          </div>
          {data.paymentMethod === 'zelle' && <p className="mt-3 text-sm leading-relaxed text-ink-600" data-id="element-895" data-mp="2yab9nvnygzp7">{t('zelleNote')}</p>}
        </fieldset>
      </section>

      <section className="mt-10" data-id="element-896" data-mp="2tbrovyl3ltyf">
        <h2 className="font-display text-lg font-extrabold text-ink" data-id="element-897" data-mp="2taoxlpjv5phz">{t('docsTitle')}</h2>
        <ul className="mt-3 divide-y divide-ink-200 rounded-2xl bg-white ring-1 ring-ink-200" data-id="element-898" data-mp="2paef5devx6m8">
          {docs.map(d => <li key={d.key} className="flex items-start gap-3 p-4" data-id="element-899" data-mp="2kcmp6oxsvuko">
              <FileTextIcon className="mt-0.5 h-5 w-5 shrink-0 text-ink-400" aria-hidden="true" data-id="element-900" data-mp="2fc8f71cn2ak0" />
              <div className="min-w-0 flex-1" data-id="element-901" data-mp="2adnqnz6r1fbe">
                <p className="font-semibold text-ink" data-id="element-902" data-mp="dk3sc49zy3g5">{t(d.key)}</p>
                <p className="text-sm text-ink-500" data-id="element-903" data-mp="djkbyrzzvn48">
                  {d.who} · {t(d.when)}
                </p>
              </div>
              <span className="text-right text-xs font-medium text-ink-500" data-id="element-904" data-mp="2317ozpas3ki0">{t('docStatusNot')}</span>
            </li>)}
        </ul>
      </section>

      <section className="mt-8 rounded-xl bg-ink-100 p-5" data-id="element-905" data-mp="27tiows5ir6ak">
        <h2 className="font-semibold text-ink" data-id="element-906" data-mp="1t3jtgx5cm8ys">{t('cancelTitle')}</h2>
        <p className="mt-1 text-[15px] leading-relaxed text-ink-700" data-id="element-907" data-mp="1t171dr5vmi80">{t('cancelBody')}</p>
        <p className="mt-3 text-[15px] font-semibold text-ink" data-id="element-908" data-mp="1y01x1toy019j">
          {t('lineDeposit')}: {usd(quote.deposit, lang)} — <span className="font-normal text-ink-600" data-id="element-909" data-mp="2lpw3ntio4tlc">{t('depositCredit')}</span>
        </p>
      </section>

      <section className="mt-8 rounded-2xl border-2 border-ink bg-white p-5 sm:p-6" data-id="element-910" data-mp="2cr10kqax7zm9">
        <div className="grid gap-3 sm:grid-cols-2" data-id="element-911" data-mp="2ctdemsdk5pu1">
          {[t('reserve99'), t('prepay')].map(label => <button key={label} type="button" disabled={!checkoutReady} className="flex min-h-[56px] flex-col items-center justify-center rounded-xl border-2 border-ink-200 px-4 text-ink disabled:cursor-not-allowed disabled:bg-ink-100" data-id="element-912" data-mp="11bvncsd5yioe">
              <span className="flex items-center gap-2 font-bold" data-id="element-913" data-mp="xswtp3i94ore">
                <LockIcon className="h-4 w-4 text-ink-400" aria-hidden="true" data-id="element-914" data-mp="sv3ks2pkdgnf" />
                {label}
              </span>
              {!checkoutReady && <span className="text-xs font-medium text-ink-500" data-id="element-915" data-mp="17rlz3wsyogqr">{t('notAvailable')}</span>}
            </button>)}
        </div>
        {siteConfig.shopifyCheckoutUrl && <a href={siteConfig.shopifyCheckoutUrl} onClick={goShopify} className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-ink-700 active:scale-[0.98]" data-id="element-916" data-mp="f4i0c9kgoynf">
            <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-917" data-mp="k0gl7n6kzyj3" />
            {lang === 'es' ? `Pagar depósito de ${usd(quote.deposit, lang)} en Shopify` : `Pay ${usd(quote.deposit, lang)} deposit on Shopify`}
          </a>}
        {failed && <p role="alert" className="mt-4 rounded-lg bg-magenta-soft p-3 text-sm font-medium text-ink" data-id="element-918" data-mp="320yvrj9d7wv8">
            {t('submitError')}
          </p>}
        <button type="button" onClick={send} disabled={sending} className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-magenta text-[19px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98] disabled:opacity-70" data-id="element-919" data-mp="370uxtcyi5e85">
          {sending && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-920" data-mp="3pw1i9kkp5o8j" />}
          {sending ? t('submitting') : failed ? t('retry') : t('requestEstimate')}
        </button>
        <p className="mt-3 text-sm text-ink-600" data-id="element-921" data-mp="3h04iidrjepx">{t('requestNote')}</p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm" data-id="element-922" data-mp="7kesbv7y0zdx">
          <a href={phones.ai.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-ink underline underline-offset-4" data-id="element-923" data-mp="cizjl557w8q8">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-924" data-mp="1x23fqgg9lkii" />
            {t('callKyle')}: {phones.ai.display}
          </a>
          <a href={phones.human.href} className="inline-flex min-h-[44px] items-center gap-1.5 font-semibold text-ink underline underline-offset-4" data-id="element-925" data-mp="1s0me2ipyhu0v">
            {t('humanHelp')}: {phones.human.display}
          </a>
        </div>
      </section>

      <HelpAnswers screen="stepReserve" className="mt-10" data-id="element-926" data-mp="2fs1konormxhx" />
    </div>;
}